import * as signalR from "@microsoft/signalr";
import { User } from "@/core/models/global/User";
// removed improper hook import
import type {
    ApplicationState as DomainApplicationState,
    ApplicationState,
} from "@/core/models/global/ApplicationState";
import { logError } from "./logger";
import { withBase } from "@/utils/basePath";
import { UserSettings } from "@/core/models/global/UserSettings";   

// ===== Helpers =====
function stripTrailingSlash(s: string) {
    return s.replace(/\/+$/, "");
}

function getHubUrl(): string {
    const override = import.meta.env.VITE_HUB_ABSOLUTE?.trim();
    if (override) return stripTrailingSlash(override);

    return withBase("hubs/emailNotification");
}

interface AuthenticationData {
    token: string;
    expiration: string;
}
interface LogoutData {
    message: string;
}
interface ComponentState {
    [key: string]: unknown;
}
interface Parameters {
    [key: string]: unknown;
}
interface RealTimeData {
    timestamp: string;
    value: unknown;
}

// Lightweight EventEmitter implementation
type Listener = (...args: any[]) => void;
class Emitter {
    private listeners = new Map<string, Set<Listener>>();

    on(event: string, listener: Listener) {
        let set = this.listeners.get(event);
        if (!set) {
            set = new Set();
            this.listeners.set(event, set);
        }
        set.add(listener);
    }

    off(event: string, listener?: Listener) {
        if (!listener) {
            this.listeners.delete(event);
            return;
        }
        const set = this.listeners.get(event);
        if (!set) return;
        set.delete(listener);
        if (set.size === 0) this.listeners.delete(event);
    }

    emit(event: string, ...args: any[]) {
        const set = this.listeners.get(event);
        if (!set) return;
        // Copy to avoid mutation during iteration
        Array.from(set).forEach((listener) => {
            try {
                listener(...args);
            } catch (e) {
                // Swallow listener errors but log
                logError(`SignalR emitter listener for '${event}' threw:`, e);
            }
        });
    }

    clear() {
        this.listeners.clear();
    }
}

// ===== Service =====
export class SignalRService {
    private connection: signalR.HubConnection | null = null;
    private isConnecting = false;
    private isStarted = false;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 10;
    private currentEmail: string | null = null;
    // Use number | null for browser timer ids
    private connectionHealthInterval: number | null = null;
    private pingInterval: number | null = null;
    private autoReconnectEnabled = true;

    // Emitter for events
    private emitter = new Emitter();

    // Throttling buffer for bulk parameters
    private bulkParametersBuffer: Parameters | null = null;
    private bulkParametersThrottleTimer: number | null = null;
    private readonly bulkParametersThrottleMs = 200; // throttle window

    constructor() {
        // Do not auto-setup or auto-start connection here.
        // Connection will be created lazily when startConnection or joinEmailGroup is called by authenticated flows.
    }

    private setupConnection(): void {
        if (this.connection) {
            return;
        }

        const hubUrl = getHubUrl();

        try {
            this.connection = new signalR.HubConnectionBuilder()
                .withUrl(hubUrl, {
                    withCredentials: true,
                })
                .withAutomaticReconnect([0, 1000, 2000, 5000, 10000, 15000, 30000])
                .configureLogging(import.meta.env.DEV ? signalR.LogLevel.Debug : signalR.LogLevel.Information)
                .build();

            this.setupEventHandlers();

            // NOTE: health monitoring is opt-in and must be started by the React layer
        } catch (error) {
            logError("SignalR: Failed to setup connection:", error);
            this.emitConnectionStateChanged("Setup Failed");
        }
    }

    private setupEventHandlers(): void {
        if (!this.connection) return;

        this.connection.onreconnecting((_error) => {
            this.emitConnectionStateChanged("Reconnecting");
        });

        this.connection.onreconnected(async (_connectionId) => {
            this.reconnectAttempts = 0;
            this.isStarted = true;
            this.emitConnectionStateChanged("Connected");
            if (this.currentEmail) await this.joinEmailGroup(this.currentEmail);
        });

        this.connection.onclose(async (_error) => {
            this.isStarted = false;
            this.emitConnectionStateChanged("Disconnected");

            if (this.autoReconnectEnabled && this.reconnectAttempts < this.maxReconnectAttempts) {
                this.reconnectAttempts++;
                const delay = Math.min(2000 * this.reconnectAttempts, 30000);
                setTimeout(() => this.startConnection(), delay);
            }
        });

        // Server -> client events: forward to emitter
        this.connection.on("EmailVerified", (userData: User) => {
            this.emitter.emit("EmailVerified", userData);
        });

        this.connection.on("UserUpdated", (userData: User) => {
            this.emitter.emit("UserUpdated", userData);
        });

        // App/params/state
        this.connection.on("ParameterUpdated", (parameterName: string, value: unknown) => {
            this.emitter.emit("ParameterUpdated", parameterName, value);
        });

        this.connection.on("BulkParametersUpdated", (parameters: Parameters) => {
            // Coalesce/buffer frequent bulk updates to avoid high-frequency emissions
            this.queueBulkParameters(parameters);
        });

        this.connection.on("ComponentStateUpdated", (componentId: string, state: ComponentState) => {
            this.emitter.emit("ComponentStateUpdated", componentId, state);
        });

        this.connection.on("UserSettingsUpdated", (settings: UserSettings) => {
            this.emitter.emit("UserSettingsUpdated", settings);
        });

        this.connection.on("ApplicationStateUpdated", (state: ApplicationState) => {
            this.emitter.emit("ApplicationStateUpdated", state);
        });

        this.connection.on("RealTimeDataUpdated", (data: RealTimeData) => {
            this.emitter.emit("RealTimeDataUpdated", data);
        });

        // Connection/utility events
        this.connection.on("Connected", (_data: unknown) => {
            this.emitConnectionStateChanged("Connected");
        });
        this.connection.on("GroupJoined", (_data: unknown) => { this.emitter.emit("GroupJoined", _data); });
        this.connection.on("GroupLeft", (_data: unknown) => { this.emitter.emit("GroupLeft", _data); });
        this.connection.on("Pong", (_data: unknown) => { this.emitter.emit("Pong", _data); });
        this.connection.on("HealthCheck", () => void this.respondToHealthCheck());
        this.connection.on("HealthCheckAck", () => { this.emitter.emit("HealthCheckAck"); });
        this.connection.on("ParameterUpdateConfirmed", (_d: unknown) => { this.emitter.emit("ParameterUpdateConfirmed", _d); });
        this.connection.on("BulkParametersUpdateConfirmed", (_d: unknown) => { this.emitter.emit("BulkParametersUpdateConfirmed", _d); });
        this.connection.on("ComponentStateUpdateConfirmed", (_d: unknown) => { this.emitter.emit("ComponentStateUpdateConfirmed", _d); });
        this.connection.on("ParameterGroupJoined", (_d: unknown) => { this.emitter.emit("ParameterGroupJoined", _d); });
        this.connection.on("ParameterGroupLeft", (_d: unknown) => { this.emitter.emit("ParameterGroupLeft", _d); });
        this.connection.on("ConnectionInfo", (_d: unknown) => { this.emitter.emit("ConnectionInfo", _d); });
        this.connection.on("ConnectionTestResult", (_d: unknown) => { this.emitter.emit("ConnectionTestResult", _d); });
        this.connection.on("RefreshUserDataRequested", (_: unknown) => {
            this.requestUserDataRefresh();
        });

        this.connection.on("InterviewBooked", (payload: InterviewBookedPayload) => {
            this.emitter.emit("InterviewBooked", payload);
        });
        this.connection.on("DocumentStatusChanged", (payload: DocumentStatusChangedPayload) => {
            this.emitter.emit("DocumentStatusChanged", payload);
        });
        this.connection.on("Error", (error: unknown) => {
            logError("SignalR: Server error", error);
            this.emitter.emit("Error", error);
        });
    }

    // Buffer/merge and emit bulk parameters after throttle window
    private queueBulkParameters(parameters: Parameters) {
        if (!this.bulkParametersBuffer) {
            // shallow clone
            this.bulkParametersBuffer = { ...(parameters || {}) };
        } else {
            // merge incoming keys
            Object.assign(this.bulkParametersBuffer, parameters || {});
        }

        if (this.bulkParametersThrottleTimer) {
            return; // timer already scheduled
        }

        this.bulkParametersThrottleTimer = window.setTimeout(() => {
            try {
                if (this.bulkParametersBuffer) {
                    this.emitter.emit("BulkParametersUpdated", this.bulkParametersBuffer);
                }
            } finally {
                this.bulkParametersBuffer = null;
                if (this.bulkParametersThrottleTimer) {
                    clearTimeout(this.bulkParametersThrottleTimer);
                    this.bulkParametersThrottleTimer = null;
                }
            }
        }, this.bulkParametersThrottleMs) as unknown as number;
    }

    public addEventListener(eventName: string, handler: (...args: unknown[]) => void): void {
        this.emitter.on(eventName, handler as Listener);
    }
    public removeEventListener(eventName: string, handler?: (...args: unknown[]) => void): void {
        this.emitter.off(eventName, handler as Listener | undefined);
    }

    public async invokeServerMethod<TResult>(methodName: string, ...args: unknown[]): Promise<TResult> {
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
            try {
                const result = await this.connection.invoke<TResult>(methodName, ...args);
                return result;
            } catch (error) {
                logError(`SignalR: Failed to invoke '${methodName}':`, error);
                throw error;
            }
        }
        throw new Error("SignalR connection is not available");
    }

    // --- Parameter/state updates (unchanged methods) ---
    public async updateParameter(parameterName: string, value: unknown): Promise<void> {
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.connection.invoke("UpdateParameter", parameterName, value);
            } catch (error) {
                logError(`SignalR: Failed to update parameter '${parameterName}':`, error);
            }
        }
    }
    public async updateBulkParameters(parameters: Parameters): Promise<void> {
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.connection.invoke("UpdateBulkParameters", parameters);
            } catch (error) {
                logError("SignalR: Failed to update bulk parameters:", error);
            }
        }
    }
    public async updateComponentState(componentId: string, state: ComponentState): Promise<void> {
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.connection.invoke("UpdateComponentState", componentId, state);
            } catch (error) {
                logError(`SignalR: Failed to update component '${componentId}' state:`, error);
            }
        }
    }
    public async joinParameterGroup(groupName: string): Promise<void> {
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.connection.invoke("JoinParameterGroup", groupName);
            } catch (error) {
                logError(`SignalR: Failed to join parameter group '${groupName}':`, error);
            }
        }
    }
    public async leaveParameterGroup(groupName: string): Promise<void> {
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.connection.invoke("LeaveParameterGroup", groupName);
            } catch (error) {
                logError(`SignalR: Failed to leave parameter group '${groupName}':`, error);
            }
        }
    }
    public async updateUserSettings(settings: UserSettings): Promise<void> {
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.connection.invoke("UpdateUserSettings", settings);
            } catch (error) {
                logError("SignalR: Failed to update user settings:", error);
                throw error;
            }
        } else {
            throw new Error("SignalR connection is not available");
        }
    }
    public async updateApplicationState(state: DomainApplicationState | Record<string, unknown>): Promise<void> {
        if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
            try {
                await this.connection.invoke("UpdateApplicationState", state);
            } catch (error) {
                logError("SignalR: Failed to update application state:", error);
                throw error;
            }
        } else {
            throw new Error("SignalR connection is not available");
        }
    }

    private async respondToHealthCheck(): Promise<void> {
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.connection.invoke("HealthCheckResponse");
            } catch (error) {
                logError("SignalR: Failed to respond to health check", error);
            }
        }
    }

    // Health monitoring is opt-in for the React layer to control lifecycle
    public startHealthMonitoring(): void {
        if (!this.connectionHealthInterval) this.connectionHealthInterval = window.setInterval(() => this.checkConnectionHealth(), 30_000) as unknown as number;
        if (!this.pingInterval) this.pingInterval = window.setInterval(() => this.sendPing(), 60_000) as unknown as number;
    }
    public stopHealthMonitoring(): void {
        if (this.connectionHealthInterval) {
            clearInterval(this.connectionHealthInterval);
            this.connectionHealthInterval = null;
        }
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }
    }

    private async checkConnectionHealth(): Promise<void> {
        if (!this.connection) return;
        if (this.connection.state === signalR.HubConnectionState.Disconnected && this.autoReconnectEnabled) {
            await this.startConnection();
        }
    }

    private async sendPing(): Promise<void> {
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.connection.invoke("Ping");
            } catch (error) {
                logError("SignalR: Failed to send ping", error);
            }
        }
    }

    private emitConnectionStateChanged(state: string): void {
        this.emitter.emit("ConnectionStateChanged", state);
    }

    private notifyConnectionStateChanged(state: string): void {
        // backward-compatible alias
        this.emitConnectionStateChanged(state);
    }

    private async requestUserDataRefresh(): Promise<void> {
        // User data refresh requested
    }

    public async startConnection(): Promise<void> {
        if (!this.connection) this.setupConnection();

        if (this.isStarted || this.isConnecting) {
            return;
        }
        if (!this.connection) {
            logError("SignalR: connection object not available when trying to start");
            return;
        }
        if (this.connection && this.connection.state !== signalR.HubConnectionState.Disconnected) {
            return;
        }

        try {
            this.isConnecting = true;

            const connectionPromise = this.connection!.start();
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error("Connection timeout after 30 seconds")), 30000)
            );

            await Promise.race([connectionPromise, timeoutPromise]);

            this.reconnectAttempts = 0;
            this.isStarted = true;
            this.emitConnectionStateChanged("Connected");

            if (this.currentEmail) await this.joinEmailGroup(this.currentEmail);
        } catch (error) {
            logError("SignalR: Failed to connect", error);
            this.reconnectAttempts++;
            this.emitConnectionStateChanged("Disconnected");

            if (this.autoReconnectEnabled && this.reconnectAttempts < this.maxReconnectAttempts) {
                const delay = Math.min(2000 * this.reconnectAttempts, 30000);
                setTimeout(() => this.startConnection(), delay);
            } else {
                logError("SignalR: Maximum reconnection attempts reached or auto-reconnect disabled");
                this.emitConnectionStateChanged("Failed");
            }
        } finally {
            this.isConnecting = false;
        }
    }

    public async stopConnection(): Promise<void> {
        if (!this.connection) return;
        try {
            this.autoReconnectEnabled = false;
            if (this.currentEmail && this.isStarted) await this.leaveEmailGroup(this.currentEmail);
            if (this.connection.state !== signalR.HubConnectionState.Disconnected) {
                await this.connection.stop();
            }
            this.isStarted = false;
            this.emitConnectionStateChanged("Disconnected");
        } catch (error) {
            logError("SignalR: Error stopping connection", error);
        }
    }

    public enableAutoReconnect(): void {
        this.autoReconnectEnabled = true;
    }
    public disableAutoReconnect(): void {
        this.autoReconnectEnabled = false;
    }

    // Email group management
    public async joinEmailGroup(email: string | null): Promise<void> {
        if (!this.connection) this.setupConnection();
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.connection.invoke("JoinEmailGroup", email);
                this.currentEmail = email;
            } catch (error) {
                logError("SignalR: Failed to join email group", error);
            }
        } else {
            this.currentEmail = email;
            if (!this.isConnecting) await this.startConnection();
        }
    }

    public async leaveEmailGroup(email: string): Promise<void> {
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.connection.invoke("LeaveEmailGroup", email);
                if (this.currentEmail === email) this.currentEmail = null;
            } catch (error) {
                logError("SignalR: Failed to leave email group", error);
            }
        }
    }

    public async refreshUserData(email: string): Promise<void> {
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.connection.invoke("RefreshUserData", email);
            } catch (error) {
                logError("SignalR: Failed to request user data refresh", error);
            }
        }
    }

    public async getConnectionInfo(): Promise<void> {
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.connection.invoke("GetConnectionInfo");
            } catch (error) {
                logError("SignalR: Failed to get connection info", error);
            }
        }
    }

    public async testConnection(): Promise<void> {
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.connection.invoke("TestConnection");
            } catch (error) {
                logError("SignalR: Failed to test connection", error);
            }
        }
    }

    // Event handler wrappers (compatibility) - map to emitter
    public onEmailVerified(cb: (userData: User) => void): void { this.emitter.on("EmailVerified", cb as Listener); }
    public offEmailVerified(listener?: (userData: User) => void): void { this.emitter.off("EmailVerified", listener as Listener | undefined); }

    public onUserUpdated(cb: (userData: User) => void): void { this.emitter.on("UserUpdated", cb as Listener); }
    public offUserUpdated(listener?: (userData: User) => void): void { this.emitter.off("UserUpdated", listener as Listener | undefined); }

    public onConnectionStateChanged(cb: (state: string) => void): void { this.emitter.on("ConnectionStateChanged", cb as Listener); }
    public offConnectionStateChanged(listener?: (state: string) => void): void { this.emitter.off("ConnectionStateChanged", listener as Listener | undefined); }

    public onAuthenticationSuccess(cb: (data: AuthenticationData) => void): void { this.emitter.on("AuthenticationSuccess", cb as Listener); }
    public offAuthenticationSuccess(listener?: (data: AuthenticationData) => void): void { this.emitter.off("AuthenticationSuccess", listener as Listener | undefined); }

    public onLogoutSuccess(cb: (data: LogoutData) => void): void { this.emitter.on("LogoutSuccess", cb as Listener); }
    public offLogoutSuccess(listener?: (data: LogoutData) => void): void { this.emitter.off("LogoutSuccess", listener as Listener | undefined); }

    public onParameterUpdated<T>(cb: (parameterName: string, value: T) => void): void { this.emitter.on("ParameterUpdated", cb as Listener); }
    public offParameterUpdated(listener?: (parameterName: string, value: unknown) => void): void { this.emitter.off("ParameterUpdated", listener as Listener | undefined); }

    public onBulkParametersUpdated(cb: (parameters: Parameters) => void): void { this.emitter.on("BulkParametersUpdated", cb as Listener); }
    public offBulkParametersUpdated(listener?: (parameters: Parameters) => void): void { this.emitter.off("BulkParametersUpdated", listener as Listener | undefined); }

    public onComponentStateUpdated(cb: (componentId: string, state: ComponentState) => void): void { this.emitter.on("ComponentStateUpdated", cb as Listener); }
    public offComponentStateUpdated(listener?: (componentId: string, state: ComponentState) => void): void { this.emitter.off("ComponentStateUpdated", listener as Listener | undefined); }

    public onInterviewBooked(cb: (p: InterviewBookedPayload) => void): void { this.emitter.on("InterviewBooked", cb as Listener); }
    public offInterviewBooked(listener?: (p: InterviewBookedPayload) => void): void { this.emitter.off("InterviewBooked", listener as Listener | undefined); }

    public onDocumentStatusChanged(cb: (p: DocumentStatusChangedPayload) => void): void { this.emitter.on("DocumentStatusChanged", cb as Listener); }
    public offDocumentStatusChanged(listener: (p: DocumentStatusChangedPayload) => void): void { this.emitter.off("DocumentStatusChanged", listener as Listener); }

    public getConnectionState(): string {
        if (!this.connection) return "Disconnected";
        switch (this.connection.state) {
            case signalR.HubConnectionState.Connected: return "Connected";
            case signalR.HubConnectionState.Connecting: return "Connecting";
            case signalR.HubConnectionState.Disconnected: return "Disconnected";
            case signalR.HubConnectionState.Disconnecting: return "Disconnecting";
            case signalR.HubConnectionState.Reconnecting: return "Reconnecting";
            default: return "Unknown";
        }
    }

    public isConnected(): boolean {
        return this.connection?.state === signalR.HubConnectionState.Connected;
    }
    public getCurrentEmail(): string | null {
        return this.currentEmail;
    }
    public getReconnectAttempts(): number {
        return this.reconnectAttempts;
    }

    public getDiagnosticInfo(): object {
        const hubUrl = getHubUrl();
        return {
            connectionState: this.getConnectionState(),
            isConnected: this.isConnected(),
            isConnecting: this.isConnecting,
            isStarted: this.isStarted,
            reconnectAttempts: this.reconnectAttempts,
            maxReconnectAttempts: this.maxReconnectAttempts,
            currentEmail: this.currentEmail,
            autoReconnectEnabled: this.autoReconnectEnabled,
            environment: {
                isDev: import.meta.env.DEV,
                mode: import.meta.env.MODE,
                baseUrl: withBase(""),
                hubUrl,
                location: typeof window !== 'undefined' ? window.location.href : undefined,
            },
            connection: {
                exists: !!this.connection,
                state: this.connection?.state,
                connectionId: this.connection?.connectionId,
                baseUrl: (this.connection as unknown as { baseUrl?: string })?.baseUrl,
            },
            intervals: {
                healthMonitoring: !!this.connectionHealthInterval,
                ping: !!this.pingInterval,
            },
            timestamp: new Date().toISOString(),
        };
    }

    public async performConnectionTest(): Promise<Record<string, unknown>> {
        const diagnostics = this.getDiagnosticInfo();
        const tests: Record<string, unknown> = {};

        tests.connectionExists = {
            passed: !!this.connection,
            message: this.connection ? "Connection object exists" : "Connection object is null",
        };

        tests.connectionState = {
            passed: this.isConnected(),
            state: this.getConnectionState(),
            message: `Connection state: ${this.getConnectionState()}`,
        };

        if (this.isConnected()) {
            try {
                await this.testConnection();
                tests.hubCommunication = { passed: true, message: "Hub communication successful" };
            } catch (error) {
                tests.hubCommunication = { passed: false, message: `Hub communication failed: ${(error as Error)?.message ?? error}` };
            }
        }

        return { diagnostics, tests };
    }

    public async cleanup(): Promise<void> {
        this.stopHealthMonitoring();

        this.emitter.clear();

        await this.stopConnection();

        this.isStarted = false;
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.currentEmail = null;
        this.connection = null;
    }
}

/** Pushed after a background refresh changed a document version's stored PandaDoc status (for example to "document.completed"). */
export type DocumentStatusChangedPayload = {
    applicantDocumentVersionId?: number;
    status?: string | null;
    at?: string;
};

export type InterviewBookedPayload = {
    applicantId?: number;
    booked?: boolean;
    details?: unknown;
    at?: string;
};

// Singleton
export const signalRService = new SignalRService();

// Expose for debugging
if (typeof window !== "undefined") {
    (window as any).signalRService = signalRService;

    window.addEventListener("beforeunload", () => {
        try {
            signalRService.disableAutoReconnect();
            if (signalRService.isConnected()) {
                signalRService.stopConnection().catch(() => { });
            }
        } catch (error) {
            logError("SignalR: Error during page unload cleanup:", error);
        }
    });
}

// Removed auto-start. Connection will start only when explicitly requested (e.g., GlobalContext when authenticated).
